import React, { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { publicGet } from "../admin/api";
import CapabilityBadges, { CAPABILITY_LABELS } from "../components/brand/CapabilityBadges";
import { setSeo } from "../lib/seo";

export const RUNS_LABEL = { local_stdio: "Local", remote: "Remote", both: "Local and remote", unknown: "Unknown" };
export const STATUS_TONE = { reviewed_clean: "safe", reviewed_warnings: "warning", under_review: "neutral", outdated: "neutral" };

export default function Servers() {
  const [sp, setSp] = useSearchParams();
  const [data, setData] = useState(null);
  const [err, setErr] = useState("");
  const f = { q: sp.get("q") || "", capability: sp.get("capability") || "", runs_where: sp.get("runs_where") || "", status: sp.get("status") || "" };
  const qs = useMemo(() => {
    const p = new URLSearchParams();
    Object.entries(f).forEach(([k, v]) => { if (v) p.set(k, v); });
    return p.toString();
  }, [f.q, f.capability, f.runs_where, f.status]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    publicGet(`/public/servers${qs ? `?${qs}` : ""}`).then(setData).catch((ex) => setErr(ex.message));
  }, [qs]);
  useEffect(() => {
    setSeo({ title: "MCP server profiles — HerdSafe", url: "https://herdsafe.ai/servers",
             description: "What MCP servers can access, where they run, which secrets they need and which domains their code contacts. Reviewed by HerdSafe." });
  }, []);
  const set = (k) => (e) => { const p = new URLSearchParams(sp); if (e.target.value) p.set(k, e.target.value); else p.delete(k); setSp(p, { replace: true }); };
  return (
    <section data-testid="servers-page" className="space-y-6">
      <h1 className="hs-display text-3xl md:text-4xl">MCP server profiles</h1>
      <p className="text-muted hs-prose">What a server can reach, where it runs, which secrets it asks for and which domains its code contacts. Each
        profile is approved by a person. A profile describes capabilities, not the author's intent.</p>
      <form role="search" className="hs-card grid gap-4 md:grid-cols-4" onSubmit={(e) => e.preventDefault()}>
        <div className="md:col-span-1">
          <label className="hs-label" htmlFor="sv-q">Search</label>
          <input id="sv-q" data-testid="servers-search" className="hs-input" value={f.q} onChange={set("q")} />
        </div>
        <div>
          <label className="hs-label" htmlFor="sv-cap">Access</label>
          <select id="sv-cap" data-testid="servers-capability" className="hs-input" value={f.capability} onChange={set("capability")}>
            <option value="">Any</option>
            {Object.entries(CAPABILITY_LABELS).map(([c, l]) => <option key={c} value={c}>{l}</option>)}
          </select>
        </div>
        <div>
          <label className="hs-label" htmlFor="sv-runs">Runs</label>
          <select id="sv-runs" data-testid="servers-runs" className="hs-input" value={f.runs_where} onChange={set("runs_where")}>
            <option value="">Anywhere</option><option value="local_stdio">Locally</option><option value="remote">Remotely</option>
          </select>
        </div>
        <div>
          <label className="hs-label" htmlFor="sv-status">Status</label>
          <select id="sv-status" data-testid="servers-status" className="hs-input" value={f.status} onChange={set("status")}>
            <option value="">Any</option><option value="reviewed_clean">Reviewed, no malicious behaviour found</option>
            <option value="reviewed_warnings">Reviewed, warnings</option><option value="under_review">Under review</option>
            <option value="outdated">Outdated</option>
          </select>
        </div>
      </form>
      {err && <div className="hs-text-danger" role="alert">{err}</div>}
      {!data && !err && <div className="text-muted">Loading…</div>}
      {data && (
        <ul className="grid gap-4 md:grid-cols-2" data-testid="servers-list">
          {data.servers.map((s) => (
            <li key={s.slug} className="hs-card" data-testid={`server-card-${s.slug}`}>
              <h2 className="hs-display text-xl m-0"><Link className="hs-link" to={`/servers/${s.slug}`}><span className="hs-mono">{s.name}</span></Link></h2>
              <p className="text-muted mt-2 mb-3">{s.what_it_does}</p>
              <div className="flex flex-wrap gap-2 items-center">
                <span className={`hs-badge hs-badge-${STATUS_TONE[s.status] || "neutral"}`}>{s.status_text}</span>
                <span className="hs-badge">{RUNS_LABEL[s.runs_where] || s.runs_where}</span>
                <CapabilityBadges capabilities={s.capabilities} />
              </div>
              <div className="hs-hint mt-2">Last checked {(s.last_checked || "").slice(0, 10) || "—"}</div>
            </li>))}
          {data.servers.length === 0 && <li className="text-muted">No profiles match.</li>}
        </ul>)}
    </section>
  );
}
